import { CloseRounded } from "@mui/icons-material";
import { Alert, IconButton } from "@mui/joy";
import { useState } from "react";
import { FieldErrors, FieldValues, useFormState } from "react-hook-form";

export const flattenErrors = (errors: FieldErrors<FieldValues>) => {
  // just do one level for now
  const flattenedErrors: FieldErrors<FieldValues> = {};
  for (const key in errors) {
    const error = errors[key];
    if (error?.type) {
      flattenedErrors[key] = error;
    } else {
      for (const subKey in error) {
        // oxlint-disable-next-line typescript/ban-ts-comment
        // @ts-ignore
        const subError = error[subKey];
        flattenedErrors[`${key}.${subKey}`] = subError;
      }
    }
  }
  return flattenedErrors;
};

export default function FormErrorAlert() {
  const formState = useFormState();
  // Dismissing hides the alert for that submit only, so the next failed one shows it again.
  const [dismissedSubmitCount, setDismissedSubmitCount] = useState<number | null>(null);
  const isDismissed = dismissedSubmitCount === formState.submitCount;
  let errorMessage = "";

  const didFailSubmit = formState.isSubmitted && !formState.isSubmitSuccessful;
  // Not memoised: `setError` adds to the errors object in place, so a memo keyed on it would miss
  // errors set after a failed request.
  const flattenedErrors = flattenErrors(formState.errors);
  const errorsCount = Object.keys(flattenedErrors).length;

  if (didFailSubmit && !formState.isSubmitting) {
    if (errorsCount === 0) {
      errorMessage = "There was a problem saving. Please try again.";
    } else if (errorsCount === 1) {
      const errorValueMessage = Object.values(flattenedErrors)[0]?.message;
      if (errorValueMessage) {
        errorMessage = `Error: ${errorValueMessage}`;
      } else {
        errorMessage = "Couldn’t save. Please correct the errors and try again.";
      }
    } else {
      errorMessage = "Couldn't save. Please correct the errors and try again.";
    }
  } else {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  return (
    <Alert
      color="danger"
      sx={{ position: "sticky", top: 1, zIndex: 10 }}
      endDecorator={
        <IconButton
          variant="soft"
          color="danger"
          onClick={() => setDismissedSubmitCount(formState.submitCount)}
        >
          <CloseRounded />
        </IconButton>
      }
    >
      {errorMessage}
    </Alert>
  );
}
